import * as React from "react";
import {makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import {IData as ClientListData} from '../../../store/client/clientList';
import {DopTable, IColumnDefinition, RowState} from '../../organisms/DopTable';
import {DetailsButton} from '../../atoms/buttons/DetailsButton';
import {EditButton} from '../../atoms/buttons/EditButton';
import {IPaginatedReportData} from '../../../entities/reports/IReportData';

// All the incoming values
interface IProps {
    title: string;
    data: IPaginatedReportData<ClientListData>;
    loading: boolean;
    hasError: boolean;
    errorDetails: string;
}

// Outgoing functions
interface IDispatch {
    onClickDetails: (client: ClientListData) => void;
    onClickEdit: (client: ClientListData) => void;
    onRefresh: () => void;
    onSearch?: (searchTerm: string) => void;
    onLoadMore?: () => void;
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({}));

// Actual Component
export type AllProps = IProps & IDispatch;
export const ClientList = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tClient } = useTranslation('client');

    const columns: IColumnDefinition<ClientListData>[] = [
        { 
            title: tDatatypes('lastNameLabel'), 
            align: "left",
            renderer: (value) => value.lastName,
            sorter: (a, b) => a.lastName.localeCompare(b.lastName),
            searchable: true
        },
        { 
            title: tDatatypes('firstNameLabel'), 
            align: "left", 
            renderer: (value) => value.firstName,
            sorter: (a, b) => a.firstName.localeCompare(b.firstName),
            searchable: true
        },
        { 
            title: tDatatypes('emailLabel'), 
            align: "left", 
            renderer: (value) => value.email || '-',
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
            searchable: true
        },
        { 
            title: tDatatypes('homePhoneLabel'), 
            align: "left", 
            renderer: (value) => value.homePhone || '-',
            sorter: (a, b) => (a.homePhone || '').localeCompare(b.homePhone || ''),
            searchable: true
        },
        { 
            title: tDatatypes('mobilePhoneLabel'), 
            align: "left", 
            renderer: (value) => value.mobilePhone || '-',
            sorter: (a, b) => (a.mobilePhone || '').localeCompare(b.mobilePhone || ''),
            searchable: true
        },
        { 
            title: tDatatypes('cityLabel'), 
            align: "left", 
            renderer: (value) => {
                if (value.residencyCityName && value.residencyCityPostalCode) {
                    return `${value.residencyCityPostalCode} ${value.residencyCityName}`;
                }
                return value.residencyCityName || value.residencyCityPostalCode || '-';
            },
            sorter: (a, b) => (a.residencyCityName || '').localeCompare(b.residencyCityName || ''),
            searchable: true
        },
        { 
            title: tCommon('actions'), 
            align: "right", 
            renderer: (value) => (
                <>
                    <DetailsButton onClick={() => props.onClickDetails(value)} />
                    <EditButton onClick={() => props.onClickEdit(value)} />
                </>
            ),
            searchable: false
        },
    ]

    return (<DopTable 
                data={{
                    rows: props.data,
                    columns: columns,
                }}
                toolbar={{
                    title: props.title,
                    onRefresh: props.onRefresh,
                    ...(props.onSearch && { onSearch: props.onSearch }),
                    searchPlaceholder: tClient('searchPlaceholder')
                }}
                loading={props.loading}
                hasError={props.hasError}
                errorDetails={props.errorDetails}
                onClickRow={(row) => props.onClickDetails(row)}
                getRowState={() => RowState.Normal}
                onLoadMore={props.onLoadMore}
            />)
}

